import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Text } from 'src/design/atoms/Text';
import { ChatSegment } from 'src/api/queries/chat/useChatConversationQueries';
import { ChatChart } from 'src/app/chat/ChatChart';
import { ChatMarkdown } from 'src/app/chat/ChatMarkdown';
import { ChatTable } from 'src/app/chat/ChatTable';
import { resolveEntityRefs } from 'src/app/chat/entityRefs';
import { MemoryCard } from 'src/app/chat/MemoryCard';
import { stripMemoryMarkers } from 'src/app/chat/memoryMarkers';
import { memorySegmentLabel } from 'src/app/chat/memoryNotes';
import { SqlDisclosure } from 'src/app/chat/SqlDisclosure';
import type { ITranscriptFormatOptions } from 'src/app/chat/transcript';
import type { ITranscriptPrintPalette } from 'src/app/chat/TranscriptPrintDocument';
import {
  ChartSection,
  TableSection,
} from 'src/app/chat/TranscriptPrintSections';
import {
  chartToMarkdown,
  formatToolRowCount,
  markdownInline,
  tableToMarkdown,
} from 'src/app/chat/transcriptSegmentFormat';

export interface ISegmentRenderContext {
  isUser: boolean;
  conversationId?: string | null;
  /** False while the reply streams or after it failed; hides the query CSV downloads. */
  canExport: boolean;
  /** Number of run_sql calls in earlier segments, so query numbers continue across the reply. */
  queryNumberOffset: number;
  readOnly?: boolean;
  streaming?: boolean;
  onRefClick?: () => void;
}

export interface ISegmentExportContext {
  role: string;
  options: ITranscriptFormatOptions;
}

export interface ISegmentPrintContext extends ISegmentExportContext {
  palette: ITranscriptPrintPalette;
}

export interface ISegmentKind<S extends ChatSegment> {
  render: (segment: S, context: ISegmentRenderContext) => React.ReactElement;
  /** Null when the segment adds nothing to the export. */
  toMarkdown: (segment: S, context: ISegmentExportContext) => string | null;
  toPrint: (segment: S, context: ISegmentPrintContext) => React.ReactNode;
}

type SegmentKinds = {
  [K in ChatSegment['kind']]: ISegmentKind<Extract<ChatSegment, { kind: K }>>;
};

/** @registry Per-kind chat segment behavior (`render` in the message bubble, `toMarkdown` for the transcript export, `toPrint` for the print document); a segment kind without an entry fails to compile. */
export const segmentKinds: SegmentKinds = {
  text: {
    render: (segment, { isUser, streaming, onRefClick }) =>
      isUser ? (
        <Text style={{ whiteSpace: 'pre-wrap' }} c="white">
          {segment.text}
        </Text>
      ) : (
        <ChatMarkdown
          text={segment.text}
          streaming={streaming}
          onRefClick={onRefClick}
        />
      ),
    toMarkdown: (segment, { role, options }) => {
      const text =
        role === 'assistant'
          ? resolveEntityRefs(
              stripMemoryMarkers(segment.text),
              options.appOrigin,
            )
          : segment.text;
      return text.length > 0 ? text : null;
    },
    toPrint: (segment, { role, options }) => {
      if (role === 'user') {
        return segment.text.length > 0 ? (
          <p className="user-text">{segment.text}</p>
        ) : null;
      }
      const text = stripMemoryMarkers(segment.text);
      if (text.length === 0) return null;
      return (
        <div className="markdown">
          <Markdown remarkPlugins={[remarkGfm]}>
            {resolveEntityRefs(text, options.appOrigin)}
          </Markdown>
        </div>
      );
    },
  },
  tools: {
    render: (segment, { conversationId, canExport, queryNumberOffset }) => (
      <SqlDisclosure
        activity={segment.activity}
        conversationId={conversationId}
        canExport={canExport}
        queryNumberOffset={queryNumberOffset}
      />
    ),
    toMarkdown: (segment, { options }) =>
      segment.activity.length > 0
        ? [
            '**Queries run**',
            '',
            ...segment.activity.map((activity) => {
              const rows = formatToolRowCount(activity, options);
              return `- \`${activity.name}\`${rows ? `: ${rows}` : ''}`;
            }),
          ].join('\n')
        : null,
    toPrint: (segment, { options }) =>
      segment.activity.length > 0 ? (
        <>
          <p className="queries-label">Queries run</p>
          <ul className="queries">
            {segment.activity.map((activity, activityIndex) => {
              const rows = formatToolRowCount(activity, options);
              return (
                <li key={activityIndex}>
                  <code>{activity.name}</code>
                  {rows ? `: ${rows}` : ''}
                </li>
              );
            })}
          </ul>
        </>
      ) : null,
  },
  chart: {
    render: (segment) => <ChatChart spec={segment.chart} />,
    toMarkdown: (segment) => chartToMarkdown(segment.chart),
    toPrint: (segment, { palette }) => (
      <ChartSection spec={segment.chart} palette={palette} />
    ),
  },
  table: {
    render: (segment, { conversationId, canExport, onRefClick }) => (
      <ChatTable
        spec={segment.table}
        conversationId={conversationId}
        canExport={canExport}
        onRefClick={onRefClick}
      />
    ),
    toMarkdown: (segment, { options }) =>
      tableToMarkdown(segment.table, options.appOrigin),
    toPrint: (segment, { options }) => (
      <TableSection spec={segment.table} appOrigin={options.appOrigin} />
    ),
  },
  memory: {
    render: (segment, { readOnly }) => (
      <MemoryCard
        memory={segment.memory}
        action={segment.memoryAction}
        readOnly={readOnly}
      />
    ),
    toMarkdown: (segment) =>
      `_${memorySegmentLabel(
        segment.memory,
        segment.memoryAction,
      )}:_ ${markdownInline(segment.memory.text)}`,
    toPrint: (segment) => (
      <p className="memory">
        <span className="memory-label">
          {memorySegmentLabel(segment.memory, segment.memoryAction)}:
        </span>{' '}
        {segment.memory.text}
      </p>
    ),
  },
};

const kindOf = (segment: ChatSegment): ISegmentKind<ChatSegment> =>
  segmentKinds[segment.kind] as ISegmentKind<ChatSegment>;

export const renderSegment = (
  segment: ChatSegment,
  context: ISegmentRenderContext,
): React.ReactElement => kindOf(segment).render(segment, context);

export const segmentToMarkdown = (
  segment: ChatSegment,
  context: ISegmentExportContext,
): string | null => kindOf(segment).toMarkdown(segment, context);

export const segmentToPrint = (
  segment: ChatSegment,
  context: ISegmentPrintContext,
): React.ReactNode => kindOf(segment).toPrint(segment, context);
